import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FiCheckCircle, FiXCircle, FiClock, FiAlertTriangle, FiSearch,
  FiFilter, FiDownload, FiEdit, FiTrash2, FiDollarSign, FiCalendar, FiEye
} from 'react-icons/fi';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@components/common';

// Types
interface Subscription {
  id: string;
  clientName: string;
  clientId: string;
  plan: 'starter' | 'professional' | 'premium' | 'enterprise';
  status: 'active' | 'trial' | 'expired' | 'suspended' | 'cancelled';
  startDate: string;
  endDate: string;
  monthlyPrice: number;
  users: number;
  maxUsers: number;
  autoRenewal: boolean;
  nextBillingDate?: string;
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const TabsContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.sm};
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
`;

const Tab = styled.button<{ $active?: boolean }>`
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background: ${props => props.$active ? theme.colors.primary : 'transparent'};
  color: ${props => props.$active ? 'white' : theme.colors.text};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${props => props.$active ? theme.colors.primary : theme.colors.surface};
  }

  svg {
    font-size: 18px;
  }
`;

const FilterSection = styled.div`
  background: white;
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  display: flex;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;

  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${theme.colors.text}60;
  }

  input {
    width: 100%;
    padding: ${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.sm} 40px;
    border: 2px solid ${theme.colors.surface};
    border-radius: ${theme.borderRadius.md};
    font-size: ${theme.typography.sizes.sm};
    font-family: ${theme.typography.fontFamily};
    transition: all ${theme.transitions.fast};

    &:focus {
      outline: none;
      border-color: ${theme.colors.primary};
    }
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;
`;

const Select = styled.select`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 2px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
`;

const StatCard = styled.div<{ color: string }>`
  background: white;
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  border-left: 4px solid ${props => props.color};
`;

const StatValue = styled.div`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}70;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  text-align: left;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text}90;
  background: ${theme.colors.surface};
  border-bottom: 2px solid ${theme.colors.surface};
`;

const Td = styled.td`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  border-bottom: 1px solid ${theme.colors.surface};
`;

const Tr = styled.tr`
  transition: background ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface}30;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};

  ${props => {
    switch (props.status) {
      case 'active':
        return `
          background: ${theme.colors.success}20;
          color: ${theme.colors.success};
        `;
      case 'trial':
        return `
          background: ${theme.colors.info}20;
          color: ${theme.colors.info};
        `;
      case 'expired':
        return `
          background: ${theme.colors.error}20;
          color: ${theme.colors.error};
        `;
      case 'suspended':
        return `
          background: ${theme.colors.warning}20;
          color: ${theme.colors.warning};
        `;
      case 'cancelled':
        return `
          background: ${theme.colors.text}20;
          color: ${theme.colors.text}70;
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.text};
        `;
    }
  }}
`;

const PlanBadge = styled.span<{ plan: string }>`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};

  ${props => {
    switch (props.plan) {
      case 'starter':
        return `
          background: ${theme.colors.primary};
          color: white;
        `;
      case 'professional':
        return `
          background: ${theme.colors.primary};
          color: white;
        `;
      case 'premium':
        return `
          background: ${theme.colors.accent};
          color: white;
        `;
      case 'enterprise':
        return `
          background: ${theme.colors.info};
          color: white;
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.text};
        `;
    }
  }}
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const ActionButton = styled.button`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: transparent;
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.text}80;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: ${theme.typography.sizes.xs};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary};
    color: white;
    border-color: ${theme.colors.primary};
  }
`;

const UsageBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${theme.colors.surface};
  border-radius: 3px;
  overflow: hidden;
  margin-top: ${theme.spacing.xs};
`;

const UsageFill = styled.div<{ percentage: number }>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: ${props => props.percentage > 90 ? theme.colors.error : theme.colors.success};
  transition: width ${theme.transitions.base};
`;

export const SubscriptionsManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [planFilter, setPlanFilter] = useState<string>('all');

  // Mock data
  const subscriptions: Subscription[] = [
    {
      id: '1',
      clientName: 'Office HLM Paris',
      clientId: 'CLI-001',
      plan: 'enterprise',
      status: 'active',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      monthlyPrice: 1290,
      users: 48,
      maxUsers: 50,
      autoRenewal: true,
      nextBillingDate: '2024-02-01'
    },
    {
      id: '2',
      clientName: 'Habitat Social Lyon',
      clientId: 'CLI-002',
      plan: 'premium',
      status: 'active',
      startDate: '2024-01-15',
      endDate: '2025-01-15',
      monthlyPrice: 890,
      users: 28,
      maxUsers: 30,
      autoRenewal: true,
      nextBillingDate: '2024-02-15'
    },
    {
      id: '3',
      clientName: 'Logement Social Marseille',
      clientId: 'CLI-003',
      plan: 'professional',
      status: 'trial',
      startDate: '2024-01-20',
      endDate: '2024-02-20',
      monthlyPrice: 590,
      users: 5,
      maxUsers: 20,
      autoRenewal: false
    },
    {
      id: '4',
      clientName: 'HLM Toulouse',
      clientId: 'CLI-004',
      plan: 'starter',
      status: 'expired',
      startDate: '2023-06-01',
      endDate: '2024-01-01',
      monthlyPrice: 290,
      users: 0,
      maxUsers: 10,
      autoRenewal: false
    },
    {
      id: '5',
      clientName: 'Immobilier Social Bordeaux',
      clientId: 'CLI-005',
      plan: 'professional',
      status: 'suspended',
      startDate: '2023-11-01',
      endDate: '2024-11-01',
      monthlyPrice: 590,
      users: 12,
      maxUsers: 20,
      autoRenewal: true
    }
  ];

  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesSearch = sub.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sub.clientId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    const matchesPlan = planFilter === 'all' || sub.plan === planFilter;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  const stats = {
    total: subscriptions.length,
    active: subscriptions.filter(s => s.status === 'active').length,
    trial: subscriptions.filter(s => s.status === 'trial').length,
    expired: subscriptions.filter(s => s.status === 'expired').length,
    mrr: subscriptions.filter(s => s.status === 'active').reduce((sum, s) => sum + s.monthlyPrice, 0)
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <FiCheckCircle />;
      case 'trial': return <FiClock />;
      case 'expired': return <FiXCircle />;
      case 'suspended': return <FiAlertTriangle />;
      default: return <FiXCircle />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'trial': return 'Essai';
      case 'expired': return 'Expiré';
      case 'suspended': return 'Suspendu';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  const getPlanLabel = (plan: string) => {
    switch (plan) {
      case 'starter': return 'Starter';
      case 'professional': return 'Professional';
      case 'premium': return 'Premium';
      case 'enterprise': return 'Enterprise';
      default: return plan;
    }
  };

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <Title>
            <FiCheckCircle /> Gestion des Abonnements
          </Title>
          <Button variant="primary" onClick={() => navigate('/superadmin/clients/new')}>
            Nouveau Client
          </Button>
        </Header>

        <TabsContainer>
          <Tab $active={true}>
            <FiCheckCircle /> Abonnements
          </Tab>
          <Tab $active={false} onClick={() => navigate('/superadmin/pricing')}>
            <FiDollarSign /> Tarifs
          </Tab>
        </TabsContainer>

        <StatsGrid>
          <StatCard color={theme.colors.primary}>
            <StatValue>{stats.total}</StatValue>
            <StatLabel>Total Abonnements</StatLabel>
          </StatCard>
          <StatCard color={theme.colors.success}>
            <StatValue>{stats.active}</StatValue>
            <StatLabel>Actifs</StatLabel>
          </StatCard>
          <StatCard color={theme.colors.info}>
            <StatValue>{stats.trial}</StatValue>
            <StatLabel>En Essai</StatLabel>
          </StatCard>
          <StatCard color={theme.colors.error}>
            <StatValue>{stats.expired}</StatValue>
            <StatLabel>Expirés</StatLabel>
          </StatCard>
          <StatCard color={theme.colors.accent}>
            <StatValue>{stats.mrr.toLocaleString()} FCFA</StatValue>
            <StatLabel>MRR Total</StatLabel>
          </StatCard>
        </StatsGrid>

        <FilterSection>
          <SearchInput>
            <FiSearch />
            <input
              type="text"
              placeholder="Rechercher par nom ou ID client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchInput>

          <FilterGroup>
            <FiFilter />
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="trial">Essai</option>
              <option value="expired">Expiré</option>
              <option value="suspended">Suspendu</option>
              <option value="cancelled">Annulé</option>
            </Select>

            <Select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
              <option value="all">Tous les plans</option>
              <option value="starter">Starter</option>
              <option value="professional">Professional</option>
              <option value="premium">Premium</option>
              <option value="enterprise">Enterprise</option>
            </Select>
          </FilterGroup>

          <Button variant="outline" onClick={() => console.log('Export')}>
            <FiDownload /> Exporter
          </Button>
        </FilterSection>

        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th>Client</Th>
                <Th>Plan</Th>
                <Th>Statut</Th>
                <Th>Utilisateurs</Th>
                <Th>Prix Mensuel</Th>
                <Th>Début</Th>
                <Th>Fin</Th>
                <Th>Prochain Paiement</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptions.map(sub => (
                <Tr key={sub.id}>
                  <Td>
                    <div style={{ fontWeight: 600 }}>{sub.clientName}</div>
                    <div style={{ fontSize: '11px', color: theme.colors.text + '60' }}>
                      {sub.clientId}
                    </div>
                  </Td>
                  <Td>
                    <PlanBadge plan={sub.plan}>{getPlanLabel(sub.plan)}</PlanBadge>
                  </Td>
                  <Td>
                    <StatusBadge status={sub.status}>
                      {getStatusIcon(sub.status)}
                      {getStatusLabel(sub.status)}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <div>{sub.users} / {sub.maxUsers}</div>
                    <UsageBar>
                      <UsageFill percentage={(sub.users / sub.maxUsers) * 100} />
                    </UsageBar>
                  </Td>
                  <Td>
                    <div style={{ fontWeight: 600 }}>{sub.monthlyPrice} FCFA</div>
                    {sub.autoRenewal && (
                      <div style={{ fontSize: '11px', color: theme.colors.success }}>
                        ↻ Auto-renouvellement
                      </div>
                    )}
                  </Td>
                  <Td>{new Date(sub.startDate).toLocaleDateString('fr-FR')}</Td>
                  <Td>{new Date(sub.endDate).toLocaleDateString('fr-FR')}</Td>
                  <Td>
                    {sub.nextBillingDate ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FiCalendar size={12} />
                        {new Date(sub.nextBillingDate).toLocaleDateString('fr-FR')}
                      </div>
                    ) : (
                      '-'
                    )}
                  </Td>
                  <Td>
                    <ActionButtons>
                      <ActionButton onClick={() => navigate(`/superadmin/subscriptions/${sub.id}`)}>
                        <FiEye /> Voir
                      </ActionButton>
                      <ActionButton onClick={() => navigate(`/superadmin/clients/${sub.clientId}`)}>
                        <FiEdit /> Client
                      </ActionButton>
                      <ActionButton onClick={() => console.log('Delete', sub.id)}>
                        <FiTrash2 />
                      </ActionButton>
                    </ActionButtons>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </Container>
    </DashboardLayout>
  );
};
